'use strict';

import { APIResponse } from "../APITypes";
import { csrfHeaders } from "../csrf";

export default class AuthenticationEndpoint {
    static async Login(username:string, password:string ):Promise<APIResponse<any>> {
        const response:Response =
        await fetch(
            '/api/auth/login',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...csrfHeaders()
                },
                credentials: 'include',  // F-05: Include credentials for cookie handling
                body: JSON.stringify({username, password}),
            }
        );

        if( !response.ok ) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || "Login failed.");
        }
        return await response.json();
    };

    static async CheckSession(): Promise<APIResponse<any>> {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
            const response: Response =
                await fetch(
                    '/api/auth/session',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...csrfHeaders()
                        },
                        credentials: 'include',  // F-05: Include credentials for cookie handling
                        signal: controller.signal
                    }
                );
            clearTimeout(timeout);

            if (!response.ok) {
                throw new Error("Session check failed");
            }
            return await response.json();
        } catch (err: any) {
            clearTimeout(timeout);
            let response: APIResponse<any>;
            if (err.name === "AbortError") {
                response = {errors: [{message: 'Session check timed out'}], data: null}
                return response;
            }
            response = {errors: [{message: err?.message || "Network error"}], data: null};
            return response;
        }
    }

    static async Logout( ): Promise<APIResponse<any>> {
        const response:Response =
        await fetch(
            '/api/auth/logout',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...csrfHeaders()
                },
                credentials: 'include',  // F-05: Include credentials for cookie handling
            }
        );

        if( !response.ok ) {
            throw new Error("Logout failed");
        }
        return await response.json();
    };

    static async AccountSetup( accountInfo: any  ): Promise<APIResponse<any>> {
        const response:Response =
        await fetch(
            '/api/auth/accountSetup',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...csrfHeaders()
                },
                credentials: 'include',  // F-05: Include credentials for cookie handling
                body: JSON.stringify(accountInfo),
            }
        );

        if( !response.ok ) {
            throw new Error("Login failed");
        }
        return await response.json();
    };

    static async Register( accountInfo: any  ): Promise<APIResponse<any>> {
        const response:Response =
        await fetch(
            '/api/auth/register',            
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...csrfHeaders()
                },
                body: JSON.stringify(accountInfo),
            }
        );

        if( !response.ok ) {
            throw new Error("Login failed");
        }
        return await response.json();
    };

    static async UpdateUserRole(user_id: number, user_role: string): Promise<APIResponse<any>> {
        const response = await fetch('/api/auth/users/update-role', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...csrfHeaders()
            },
            body: JSON.stringify({ user_id, user_role })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.errors?.[0]?.message || errorData.error || 'User role update failed.');
        }
        return await response.json();
    }

    static async DeleteUser(user_id: number): Promise<APIResponse<any>> {
        const response = await fetch('/api/auth/users/delete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...csrfHeaders()
            },
            body: JSON.stringify({ user_id })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.errors?.[0]?.message || errorData.error || 'User deletion failed.');
        }
        return await response.json();
    }

    static async ResetUserPassword(user_id: number, password: string): Promise<APIResponse<any>> {
        const response = await fetch('/api/auth/users/reset-password', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...csrfHeaders()
            },
            body: JSON.stringify({ user_id, password })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.errors?.[0]?.message || errorData.error || 'Password reset failed.');
        }
        return await response.json();
    }

    async ForgotPassword( name: string  ): Promise<APIResponse<any>> {
        const response:Response =
        await fetch(
            '/api/auth/forgotPassword',            
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...csrfHeaders()
                },
                body: JSON.stringify({name}),
            }
        );

        if( !response.ok ) {
            throw new Error("Login failed");
        }
        return await response.json();
    };

    static async CheckUserNameAvailability(
        user_name: string,
        signal?: AbortSignal
    ): Promise<boolean> {
        // user name requires no white space, only letters, numbers, underscores, hyphens
        // user name is always lowercase

        const validPattern = /^[a-z0-9_-]+$/;
        if (user_name !== user_name.toLowerCase()) {
            throw new Error("Username must be lowercase");
        }
        if (!validPattern.test(user_name)) {
            throw new Error("Username can only contain lowercase letters, numbers, underscores, and hyphens, with no spaces");
        }
        try {
            const response: Response =
            await fetch(
                '/api/auth/checkUserNameAvailability',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...csrfHeaders()
                    },
                    body: JSON.stringify({ user_name }),
                    signal
                }
            );

            if (!response.ok) {
                throw new Error("Check user name availability failed");
            }
            const result = await response.json();
            return result.available as boolean;
        } catch (err: any) {
            if (err.name === "AbortError") return false;
            throw err;
        }
    }
    /**
     * Reset password using a reset token and new password.
     * @param token The reset token provided to the user.
     * @param newPassword The new password to set.
     * @returns Promise resolving to the backend response or rejecting with an error.
     */
    static async ResetPassword(token: string, newPassword: string): Promise<APIResponse<any>> {
        try {
            const response: Response = await fetch(
                '/api/auth/resetPassword',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...csrfHeaders()
                    },
                    body: JSON.stringify({ token, newPassword }),
                }
            );
            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error?.message || "Password reset failed");
            }
            return await response.json();
        } catch (err: any) {
            throw new Error(err?.message || "Network error");
        }
    }
}